import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Document, Page, pdfjs } from 'react-pdf';
import { BreakpointValues, Grid } from '../app/StyleGuide';
import { useVisiblePdfPages } from '../hooks/useVisiblePdfPages';
import { isAppleTouchDevice } from '../utils/userAgent';
import EmptyState from './EmptyState';
import LoadingState from './LoadingState';
import PDFViewerToolbar from './PDFViewerToolbar';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

const Container = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .react-pdf__Page {
    width: 100%;
    padding: ${Grid._3} 0;
  }

  .react-pdf__Page canvas,
  .react-pdf__Page img.pdf-page-image {
    width: 100% !important;
    height: auto !important;
    display: block;
  }
`;

const ViewerWrapper = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  margin: 0 auto;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: ${Grid._6} 0;
`;

const ContentFrame = styled.div<{ $baseWidth: number; $zoomLevel: number }>`
  width: ${props => props.$baseWidth * props.$zoomLevel}px;
  max-width: ${props => (props.$zoomLevel > 1 ? 'none' : '100%')};
  margin: 0 auto;
  position: relative;
`;

const TOOLBAR_HIDE_DELAY_MS = 1500;

const documentOptions = {
  // Served from the pdfjs-dist package in dev and copied into the build output;
  // both are wired up by pdfjsCmapsPlugin in vite.config.mts.
  cMapUrl: '/pdfjs/cmaps/',
  cMapPacked: true,
  withCredentials: true,
  disableFontFace: true,
};

/**
 * Memory scales with (renderWidth × DPR)² per mounted page. Keep both small.
 * Canvas is CSS-scaled to the container; cap avoids multi‑MB bitmaps on wide layouts.
 */
const MAX_DPR = 2;
const PAGE_PREFETCH_MARGIN_RATIO = 0.28;
const MAX_MOUNTED_PAGES = 6;
const DEFAULT_PLACEHOLDER_HEIGHT = 800;

type PdfJsDocumentLike = {
  numPages: number;
  getPage: (n: number) => Promise<{ getViewport: (o: { scale: number }) => { width: number; height: number } }>;
};

/**
 * live canvas layers paint above the iOS scroll indicator.
 * Snapshot to <img> after render so scrolling uses normal DOM painting.
 */
function replaceCanvasWithImage(pageSlot: HTMLElement) {
  const canvas = pageSlot.querySelector('canvas');
  if (!canvas) return;

  const img = document.createElement('img');
  img.className = 'pdf-page-image';
  img.alt = '';
  img.decoding = 'async';
  img.draggable = false;
  img.src = canvas.toDataURL('image/png');
  canvas.replaceWith(img);
}

const PDFPages = React.memo(function PDFPages({
  file,
  pageNumber,
  scrollTransitionEnabled,
  wrapperRef,
  zoomLevel,
  onPageChangeFromScroll,
  onReadyToDisplay,
  onDocumentLoadSuccess,
  onDocumentLoadError,
}: {
  file: string;
  pageNumber: number;
  scrollTransitionEnabled: boolean;
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  zoomLevel: number;
  onPageChangeFromScroll: (page: number) => void;
  onReadyToDisplay: () => void;
  onDocumentLoadSuccess: (numPages: number) => void;
  onDocumentLoadError: (error: Error) => void;
}) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageWidth, setPageWidth] = useState<number>();
  const [pageHeights, setPageHeights] = useState<number[]>([]);
  const [mountedPages, setMountedPages] = useState<Set<number>>(() => new Set());
  const pdfRef = useRef<PdfJsDocumentLike | null>(null);
  const pageRefsRef = useRef<(HTMLDivElement | null)[]>([]);
  const currentPageFromScrollRef = useRef(1);
  const hasSignaledReadyRef = useRef(false);
  const snapshotPagesToImage = isAppleTouchDevice();

  // Reset per-file viewer state when the source document changes.
  useEffect(() => {
    hasSignaledReadyRef.current = false;
    pdfRef.current = null;
    setNumPages(null);
    setPageHeights([]);
    setMountedPages(new Set());
  }, [file]);

  // Start with only page 1 mounted after document metadata is ready.
  useEffect(() => {
    if (numPages == null || numPages < 1) return;
    setMountedPages(() => new Set([1]));
  }, [numPages, file]);

  // Read each page's viewport to reserve accurate placeholder heights without rendering.
  useEffect(() => {
    const pdf = pdfRef.current;
    if (!pdf || numPages == null || numPages < 1 || pageWidth == null || pageWidth <= 0) return;
    if (typeof pdf.getPage !== 'function') {
      setPageHeights(Array.from({ length: numPages }, () => DEFAULT_PLACEHOLDER_HEIGHT));
      return;
    }
    let cancelled = false;
    Promise.all(
      Array.from({ length: numPages }, (_, index) =>
        pdf
          .getPage(index + 1)
          .then(page => {
            const v1 = page.getViewport({ scale: 1 });
            const scale = pageWidth / v1.width;
            return page.getViewport({ scale }).height;
          })
          .catch(() => DEFAULT_PLACEHOLDER_HEIGHT)
      )
    )
      .then(heights => {
        if (cancelled) return;
        setPageHeights(heights);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [file, numPages, pageWidth]);

  // Track wrapper width changes so page render width stays in sync.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const updateWidth = () => {
      const cssWidth = el.clientWidth;
      if (cssWidth > 0) setPageWidth(cssWidth);
    };
    updateWidth();
    const ro = new ResizeObserver(updateWidth);
    ro.observe(el);
    return () => ro.disconnect();
  }, [zoomLevel, wrapperRef]);

  useVisiblePdfPages({
    numPages,
    pageRefsRef,
    currentPageFromScrollRef,
    setMountedPages,
    onPageChangeFromScroll,
    maxMountedPages: MAX_MOUNTED_PAGES,
    prefetchMarginRatio: PAGE_PREFETCH_MARGIN_RATIO,
    focusedPage: scrollTransitionEnabled ? pageNumber : undefined,
    layoutRevision: pageHeights.length,
  });

  useEffect(() => {
    if (!scrollTransitionEnabled) return;
    const el = pageRefsRef.current[pageNumber - 1];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [pageNumber, scrollTransitionEnabled]);

  const setPageRef = (index: number) => (el: HTMLDivElement | null) => {
    pageRefsRef.current[index] = el;
  };

  const onLoadSuccess = (pdf: PdfJsDocumentLike) => {
    pdfRef.current = pdf;
    setNumPages(pdf.numPages);
    onDocumentLoadSuccess(pdf.numPages);
  };

  const onLoadError = (err: Error) => {
    console.error(err);
    onDocumentLoadError(err);
  };

  const dpr = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, MAX_DPR) : 1;

  return (
    <Document
      key={file}
      options={documentOptions}
      file={file}
      onLoadSuccess={onLoadSuccess}
      onLoadError={onLoadError}
      loading={null}
      externalLinkTarget="_blank"
    >
      {numPages != null &&
        pageWidth != null &&
        Array.from(new Array(numPages), (_, index) => (
          <div
            key={`page_${index + 1}`}
            data-page-number={index + 1}
            ref={setPageRef(index)}
            style={{
              height:
                index === 0 || mountedPages.has(index + 1) ? 'auto' : pageHeights[index] ?? DEFAULT_PLACEHOLDER_HEIGHT,
            }}
          >
            {(index === 0 || mountedPages.has(index + 1)) && (
              <Page
                pageNumber={index + 1}
                width={pageWidth}
                devicePixelRatio={dpr}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                loading={null}
                onRenderSuccess={() => {
                  if (snapshotPagesToImage) {
                    const pageSlot = pageRefsRef.current[index];
                    if (pageSlot) replaceCanvasWithImage(pageSlot);
                  }
                  if (index === 0 && !hasSignaledReadyRef.current) {
                    hasSignaledReadyRef.current = true;
                    onReadyToDisplay();
                  }
                }}
              />
            )}
          </div>
        ))}
    </Document>
  );
});

export default function PDFViewer({ url }: { url: string }) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toolbarVisible, setToolbarVisible] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [scrollTransitionEnabled, setScrollTransitionEnabled] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [wrapperWidth, setWrapperWidth] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const viewerWrapperRef = useRef<HTMLDivElement | null>(null);

  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const defaultBaseWidth = BreakpointValues.screen_md;
  // Use container width on small screens, but cap width on larger screens
  const baseWidth = Math.min(defaultBaseWidth, wrapperWidth ?? defaultBaseWidth);
  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 2;
  const ZOOM_STEP = 0.25;

  useEffect(() => {
    const el = viewerWrapperRef.current;
    if (!el) return;
    const updateWidth = () => {
      const width = el.clientWidth;
      if (width > 0) setWrapperWidth(width);
    };
    updateWidth();
    const ro = new ResizeObserver(updateWidth);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setPageNumber(1);
    setNumPages(null);
    setError(null);
    setScrollTransitionEnabled(true);
    setZoomLevel(1);
  }, [url]);

  const handleDocumentLoadSuccess = (totalPages: number) => {
    setNumPages(totalPages);
    setPageNumber(1);
  };

  const handleReadyToDisplay = () => {
    setIsLoading(false);
  };

  const handleDocumentLoadError = (err: Error) => {
    setError(err.message || 'Unable to load PDF');
    setIsLoading(false);
  };

  const handleDownload = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handlePageChangeFromScroll = (page: number) => {
    setPageNumber(page);
    setScrollTransitionEnabled(false);
  };

  const handlePageUp = () => {
    setScrollTransitionEnabled(true);
    setPageNumber(prev => Math.max(1, prev - 1));
  };

  const handlePageDown = () => {
    setScrollTransitionEnabled(true);
    setPageNumber(prev => (numPages != null ? Math.min(numPages, prev + 1) : prev + 1));
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(ZOOM_MAX, prev + ZOOM_STEP));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(ZOOM_MIN, prev - ZOOM_STEP));
  };

  const scheduleToolbarHide = () => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(() => {
      setToolbarVisible(false);
      hideTimeoutRef.current = null;
    }, TOOLBAR_HIDE_DELAY_MS);
  };

  const handleMouseMove = () => {
    if (isLoading) return;
    setToolbarVisible(true);
    scheduleToolbarHide();
  };

  const handleScroll = () => {
    if (isLoading) return;
    setToolbarVisible(true);
    scheduleToolbarHide();
  };

  const handleMouseLeave = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setToolbarVisible(false);
  };

  useEffect(
    () => () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    },
    []
  );

  if (!url) {
    return null;
  }

  return (
    <Container onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
      {isLoading && !error && <LoadingState />}
      {error && (
        <EmptyState icon="fa-file-pdf" title="Unable to load PDF">
          <p>
            Trouble viewing the file? Download it{' '}
            <a href={url} target="_blank" rel="noopener noreferrer">
              here
            </a>
          </p>
        </EmptyState>
      )}
      <ViewerWrapper ref={viewerWrapperRef} style={{ visibility: isLoading || error ? 'hidden' : 'visible' }}>
        <ScrollArea onScroll={handleScroll}>
          <ContentFrame ref={contentRef} $baseWidth={baseWidth} $zoomLevel={zoomLevel}>
            <PDFPages
              file={url}
              pageNumber={pageNumber}
              scrollTransitionEnabled={scrollTransitionEnabled}
              wrapperRef={contentRef}
              zoomLevel={zoomLevel}
              onPageChangeFromScroll={handlePageChangeFromScroll}
              onReadyToDisplay={handleReadyToDisplay}
              onDocumentLoadSuccess={handleDocumentLoadSuccess}
              onDocumentLoadError={handleDocumentLoadError}
            />
          </ContentFrame>
        </ScrollArea>
        <PDFViewerToolbar
          visible={toolbarVisible}
          isLoading={isLoading}
          pageNumber={pageNumber}
          numPages={numPages}
          zoomLevel={zoomLevel}
          zoomMin={ZOOM_MIN}
          zoomMax={ZOOM_MAX}
          onPageUp={handlePageUp}
          onPageDown={handlePageDown}
          onZoomOut={handleZoomOut}
          onZoomIn={handleZoomIn}
          onDownload={handleDownload}
        />
      </ViewerWrapper>
    </Container>
  );
}
